import * as React from 'react';
import { t } from '@citrite/translate';
import {
	Button,
	CopyToClipboardIcon,
	notifyError,
	notifySuccess,
	WarningBanner,
} from '@citrite/web-ui-component';
import { useTheme } from '@emotion/react';
import { trackAnalyticsEvent } from 'analytics';
import { BannerPosition } from 'AdminNotificationService/createAdminNotificationClient';
import { useFormFactor } from 'App/Header/useFormFactor';
import { DaasNotificationBanner } from 'Components/AppLayout/DaasNotificationBanner';
import { isCitrixChromeApp } from 'Environment/launchResource/device';
import { copyToClipboard } from 'Environment/utils';
import { EventType } from 'Workspace/TelemetryEvents/TelemetryEventTypes';
import {
	StyledBannerText,
	StyledBannerTitle,
	StyledInlinedBanner,
} from '../AdminNotificationBanner.styled';
import {
	getBannerPositionFromFormFactor,
	shouldDisplayBanner,
} from '../AdminNotificationBannerUtils';

interface State {
	bannerTitle: string;
	bannerDescription: string;
}

export interface DevicePostureBannerProps {
	transactionId: string;
	intendedPosition: BannerPosition;
}
export interface ContentProps {
	isExpanded?: boolean;
}

const DevicePostureBannerCode = 'DevicePostureBannerCode';

function sendDevicePostureBannerEvent(bannerPosition: BannerPosition) {
	const devicePostureBannerEvent = {
		type: EventType.DevicePostureBanner,
		code: DevicePostureBannerCode,
		bannerPosition,
	};
	trackAnalyticsEvent(devicePostureBannerEvent);
}

export function DevicePostureBanner({
	transactionId,
	intendedPosition,
}: DevicePostureBannerProps) {
	const [bannerData, setBannerData] = React.useState<State>({} as State);
	const { bannerTitle, bannerDescription } = bannerData;
	const isLargeFormFactor = useFormFactor().isLargeFormFactor;
	const mastheadOffset = useTheme().mastheadOffset;
	const bannerPosition = BannerPosition.Top;

	const isBannerAvailable =
		transactionId &&
		shouldDisplayBanner(intendedPosition, bannerPosition, isLargeFormFactor);

	React.useEffect(() => {
		const title = t('Workspace:banner.device_posture.title');
		const description = t('Workspace:banner.device_posture.description', {
			transactionId: <strong>{transactionId}</strong>,
		});
		setBannerData({
			bannerTitle: title,
			bannerDescription: description,
		});
		if (isBannerAvailable) {
			sendDevicePostureBannerEvent(bannerPosition);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleCopyTransactionId = React.useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			try {
				await copyToClipboard(transactionId);
				notifySuccess(t('Workspace:mandate_native_app_screen.url_copied'));
			} catch {
				notifyError(t('Workspace:mandate_native_app_screen.url_copy_failed'));
			}
		},
		[transactionId]
	);

	const daasBannerContent = React.useCallback(
		(props: ContentProps) => (
			<StyledInlinedBanner showSingleLine={false}>
				<StyledBannerTitle
					data-testid="daas-device-posture-banner-title"
					isExpanded={props.isExpanded}
				>
					<b>{bannerTitle}</b>
				</StyledBannerTitle>

				{props.isExpanded && (
					<StyledBannerText padding={0}>
						{bannerDescription}
						{!isCitrixChromeApp() && (
							<Button.Wrapper onClick={handleCopyTransactionId}>
								<CopyToClipboardIcon />
							</Button.Wrapper>
						)}
					</StyledBannerText>
				)}
			</StyledInlinedBanner>
		),
		[bannerTitle, bannerDescription, handleCopyTransactionId]
	);

	if (!(isBannerAvailable && bannerTitle && bannerDescription)) {
		return null;
	}
	const position = getBannerPositionFromFormFactor(bannerPosition, isLargeFormFactor);

	return (
		<DaasNotificationBanner
			Content={daasBannerContent}
			bannerPosition={position}
			mastheadOffset={mastheadOffset}
			Banner={WarningBanner}
			bannerType="warning"
			isAdminBanner
		/>
	);
}
